import { useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  MessageSquare, FileText, FolderOpen, Bot, Wrench, File, Search,
} from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { useGlobalSearch, type SearchResult } from '@/hooks/useGlobalSearch';
import { useNavigation } from '@/contexts/NavigationContext';

const TYPE_META: Record<string, { label: string; icon: typeof MessageSquare }> = {
  conversation: { label: 'Conversas', icon: MessageSquare },
  message: { label: 'Mensagens', icon: MessageSquare },
  project: { label: 'Projetos', icon: FolderOpen },
  prompt: { label: 'Prompts', icon: FileText },
  agent: { label: 'Agentes', icon: Bot },
  tool: { label: 'Ferramentas', icon: Wrench },
  file: { label: 'Arquivos', icon: File },
  chat_file: { label: 'Anexos de Chat', icon: File },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CommandPalette = ({ open, onOpenChange }: Props) => {
  const { query, setQuery, results, isLoading } = useGlobalSearch();
  const { navigateToTab, navigateToProject, navigateToGlobal, navigateToPersonal, setScopeFilter, setSearchPrefill, setActiveConversationId, skipNextConversationReset, setScrollToMessageId } = useNavigation();

  // Ctrl/Cmd+K shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  // Clear on close
  useEffect(() => {
    if (!open) setQuery('');
  }, [open, setQuery]);

  const grouped = useMemo(() => {
    const map: Record<string, SearchResult[]> = {};
    for (const r of results) {
      (map[r.result_type] ??= []).push(r);
    }
    return map;
  }, [results]);

  /**
   * Navigate to a conversation by looking up its metadata and setting the correct context.
   * Works for both 'conversation' and 'message' result types.
   */
  const openConversation = useCallback(async (conversationId: string, messageIdToScroll?: string) => {
    const { data } = await supabase
      .from('conversations')
      .select('id, project_id, scope')
      .eq('id', conversationId)
      .maybeSingle();

    if (!data) return;

    skipNextConversationReset.current = true;

    if (data.project_id) {
      navigateToProject(data.project_id, true);
    } else if (data.scope === 'global') {
      navigateToGlobal();
    } else {
      navigateToPersonal();
    }

    navigateToTab('chat');
    setActiveConversationId(data.id);

    if (messageIdToScroll) {
      setScrollToMessageId(messageIdToScroll);
    }
  }, [skipNextConversationReset, navigateToProject, navigateToGlobal, navigateToPersonal, navigateToTab, setActiveConversationId, setScrollToMessageId]);

  const handleSelect = useCallback((result: SearchResult) => {
    onOpenChange(false);
    switch (result.result_type) {
      case 'conversation':
        openConversation(result.id);
        break;
      case 'message':
        if (result.parent_id) {
          openConversation(result.parent_id, result.id);
        }
        break;
      case 'file':
        if (result.parent_id) {
          navigateToProject(result.parent_id);
          navigateToTab('chat');
        }
        break;
      case 'chat_file':
        if (result.parent_id) {
          openConversation(result.parent_id);
        }
        break;
      case 'prompt':
        navigateToGlobal();
        navigateToTab('prompts');
        setScopeFilter('all');
        setSearchPrefill(result.title);
        break;
      case 'agent':
        navigateToGlobal();
        navigateToTab('agents');
        setScopeFilter('all');
        setSearchPrefill(result.title);
        break;
      case 'project':
        navigateToProject(result.id);
        navigateToTab('chat');
        break;
      case 'tool':
        navigateToGlobal();
        navigateToTab('tools');
        setScopeFilter('all');
        setSearchPrefill(result.title);
        break;
    }
  }, [onOpenChange, openConversation, navigateToProject, navigateToGlobal, navigateToTab, setScopeFilter, setSearchPrefill]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Buscar conversas, projetos, prompts, agentes..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isLoading && query.length >= 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground">Buscando...</div>
        )}
        {!isLoading && query.length >= 2 && results.length === 0 && (
          <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
        )}
        {query.length < 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Search className="h-5 w-5 text-tertiary" />
            <span>Digite ao menos 2 caracteres para buscar</span>
          </div>
        )}
        {Object.entries(grouped).map(([type, items]) => {
          const meta = TYPE_META[type] ?? { label: type, icon: Search };
          const Icon = meta.icon;
          return (
            <CommandGroup key={type} heading={meta.label}>
              {items.map((item) => (
                <CommandItem
                  key={`${type}-${item.id}`}
                  value={`${type}-${item.id}-${item.title}`}
                  onSelect={() => handleSelect(item)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate text-sm">{item.title}</span>
                    {item.subtitle && (
                      <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
};
